import { Injectable } from '@angular/core';
import structuredClone from '@ungap/structured-clone';
import { Observable, Subject, zip } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { AsyncRequestService } from 'src/app/core/async-request/async-request.service';
import { WebSocketTaskNotification } from 'src/app/core/websocket/model';
import { TaskStateInWsNotif } from 'src/app/shared/models/misc.model';
import { API_URL_COMPANIES } from 'src/app/shared/services/basic.service';
import { ErrorHandlingService } from 'src/app/shared/services/error-handling.service';

import { FilterType, QueueIdEnum } from '../shared/enums';
import { Counter, Filter, TaskListConfiguration, TaskListConfigurationParameters } from '../shared/models';

@Injectable({
  providedIn: 'root',
})
export class CountersService {
  initTabsCountersBS: Subject<TaskListConfiguration[]> = new Subject();
  initChannelsCountersBS: Subject<TaskListConfiguration[]> = new Subject();
  initByMeCountersBS: Subject<TaskListConfiguration[]> = new Subject();
  initCounterIndexesBS: Subject<any> = new Subject();
  countersBS: Subject<Counter[]> = new Subject();

  private _counters: Counter[] = [];
  private counterIndex: number;
  private currentTaskListConfiguration: TaskListConfiguration;
  private previousQueueId = '';
  private previousCountersConfigs: TaskListConfiguration[] = [];
  private previousFilters: Filter[] = [];
  private _tabsCounterIndexes: any[];
  private _channelsCounterIndexes: any[];
  private _byMeCounterIndexes: any[];

  get counters() {
    return this._counters;
  }

  get tabsCounterIndexes() {
    return this._tabsCounterIndexes;
  }

  get channelsCounterIndexes() {
    return this._channelsCounterIndexes;
  }

  get byMeCounterIndexes() {
    return this._byMeCounterIndexes;
  }

  constructor(
    private alcmeonContextService: AlcmeonContextService,
    private asyncRequestService: AsyncRequestService,
    private errorHandlingService: ErrorHandlingService
  ) {
    this.getAllCountersSubscribe();
  }

  private getCounters(taskListConfigsParams: TaskListConfiguration[]): Observable<Counter[]> {
    return this.asyncRequestService
      .post<Counter[]>(
        `${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/tasks/counts`,
        taskListConfigsParams
      )
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  initTabsCounters(
    tabsTaskListConfigsForCounters: TaskListConfiguration[],
    currentTaskListConfig: TaskListConfiguration
  ) {
    this.currentTaskListConfiguration = currentTaskListConfig;
    this.initTabsCountersBS.next(tabsTaskListConfigsForCounters);
  }

  initChannelsCounters(channelsTaskListConfigs: TaskListConfiguration[], currentTaskListConfig: TaskListConfiguration) {
    this.currentTaskListConfiguration = currentTaskListConfig;
    this.initChannelsCountersBS.next(channelsTaskListConfigs);
  }

  initByMeCounters(byMeCounterConfigs: TaskListConfiguration[], currentTaskListConfig: TaskListConfiguration) {
    this.currentTaskListConfiguration = currentTaskListConfig;
    this.initByMeCountersBS.next(byMeCounterConfigs);
  }

  /**
   * We have counters in 3 components that are completely independant, not even an angular parent in common (managed by AJS states), to do the job of mutualise BE calls.
   * And we don't want to make 3 parallels calls to "tasks/counts" endpoint (risks of 429).
   * So we wait the .next() of each component with zip(), concat all arrays, and make one call.
   * The counterpart is it's not seamless, each component have to make .next() on a subject, and subscribe another subject to receive response.
   */
  private getAllCountersSubscribe() {
    zip(this.initTabsCountersBS, this.initChannelsCountersBS, this.initByMeCountersBS).subscribe(result => {
      const countersConfigs = this.getCountersConfigs(result[0], result[1], result[2]);

      if (
        this.hasCountersConfigsChanged(countersConfigs, this.currentTaskListConfiguration.queueId) ||
        this.hasFiltersChanged()
      ) {
        this.previousQueueId = this.currentTaskListConfiguration.queueId;
        this.previousCountersConfigs = countersConfigs;
        this.previousFilters = structuredClone(this.currentTaskListConfiguration.filters);

        this.getCounters(countersConfigs).subscribe(counters => {
          this._counters = counters;
          this.countersBS.next(counters);
        });
      } else {
        this.countersBS.next(this.counters);
      }
    });
  }

  /**
   * Simplified version of above function getAllCountersSubscribe() for pushbox
   * => only one conf that doesn't change, no filters, and one component with one counter
   */
  getAllCounters(taskListConfig: TaskListConfiguration): Observable<Counter[]> {
    this.currentTaskListConfiguration = taskListConfig;
    this.previousQueueId = '';

    return this.getCounters([taskListConfig]).pipe(
      tap(counters => {
        this._counters = counters;
        this.countersBS.next(counters);
      })
    );
  }

  /**
   * "tasks/counts" endpoint is not expected to read TaskListConfiguration with children configurations.
   * So we prepare a one level array of all tabs and sub tabs configs parameters.
   * And we exclude tabs where we don't want counter.
   *
   * We keep the index of the counter array in the initial tabsTaskListConfigs object.
   */
  private getCountersConfigs(
    tabsTaskListConfigs: TaskListConfiguration[],
    channelsTaskListConfigs: TaskListConfiguration[],
    byMeTaskListConfigs: TaskListConfiguration[]
  ): TaskListConfiguration[] {
    const excludedCounters = [QueueIdEnum.AnsweredByOthers, QueueIdEnum.ArchivedByOthers];
    this.counterIndex = 0;
    this._tabsCounterIndexes = [];
    this._channelsCounterIndexes = [];
    this._byMeCounterIndexes = [];
    const paramsForCounters: TaskListConfiguration[] = [];

    /**
     * Push a config in flat array if it contains parameters and is not excluded
     * And construct an array of indexes, so the components can find the counter correponding to the tab or channel
     */
    const pushIfParametersFn = (config: TaskListConfiguration, counterIndexes: any[]) => {
      if (config.parameters && !excludedCounters.includes(config.queueId as QueueIdEnum)) {
        paramsForCounters.push({
          parameters: config.parameters,
          filters: this.currentTaskListConfiguration.filters,
        });

        counterIndexes.push(this.counterIndex);
        this.counterIndex++;
      } else {
        // -1 or undefined
        counterIndexes.push(config.counterIndex);
      }
    };

    tabsTaskListConfigs.forEach(config => {
      pushIfParametersFn({ ...config }, this._tabsCounterIndexes);

      config.children?.forEach(child => {
        pushIfParametersFn({ ...child }, this._tabsCounterIndexes);
      });
    });

    channelsTaskListConfigs.forEach(channelConfig => {
      // include channels if current tab is not excluded
      if (!excludedCounters.includes(this.currentTaskListConfiguration.queueId as QueueIdEnum)) {
        pushIfParametersFn({ ...channelConfig }, this._channelsCounterIndexes);
      } else {
        // current config can contain the previous data
        delete channelConfig.counterIndex;
        delete channelConfig.counter;
      }
    });

    if (this.currentTaskListConfiguration.queueId === QueueIdEnum.Received) {
      byMeTaskListConfigs.forEach(byMeConfig => {
        paramsForCounters.push({
          parameters: byMeConfig.parameters,
          filters: this.currentTaskListConfiguration.filters,
        });
        this._byMeCounterIndexes.push(this.counterIndex);
        this.counterIndex++;
      });
    }

    this.initCounterIndexesBS.next();
    return paramsForCounters;
  }

  /**
   * When navigating among the channels, the counters config doesn't change, so no need to make a new call.
   * But we want to refresh counters when we change site selection or tabs.
   */
  private hasCountersConfigsChanged(newConfigs: TaskListConfiguration[], newQueueId: string): boolean {
    // first load
    if (!this.counters.length) {
      return true;
    }

    // change tab
    if (newQueueId !== this.previousQueueId) {
      return true;
    }

    // change site filter : the number of configs is different (ex: 5 channels for facebook, 3 for twitter, 1 for whatsapp, etc ...)
    if (newConfigs.length !== this.previousCountersConfigs.length) {
      return true;
    }

    // same length doesn't mean same channels, check if we have the same list of sites and site groups
    // but no need to loop over all configs, arrays of sites_ids and site_group_ids are the same for all configs, so just test the first one

    // change site filter (ex: had one facebook site selected, add another one in selection)
    if (newConfigs[0].parameters.sites_ids.length !== this.previousCountersConfigs[0].parameters.sites_ids.length) {
      return true;
    }

    // change site filter (ex: had one facebook site selected, replace it with another one)
    if (
      !newConfigs[0].parameters.sites_ids.every(
        (siteId, y) => siteId === this.previousCountersConfigs[0].parameters.sites_ids[y]
      )
    ) {
      return true;
    }

    // change site filter, same logic as above with group
    if (
      newConfigs[0].parameters.site_group_ids.length !==
      this.previousCountersConfigs[0].parameters.site_group_ids.length
    ) {
      return true;
    }

    // change site filter, same logic as above with group
    if (
      !newConfigs[0].parameters.site_group_ids.every(
        (siteId, y) => siteId === this.previousCountersConfigs[0].parameters.site_group_ids[y]
      )
    ) {
      return true;
    }

    return false;
  }

  private hasFiltersChanged(): boolean {
    if (!this.previousFilters && !this.currentTaskListConfiguration.filters) {
      return false;
    }

    const previousFiltersObj: any = {};
    const currentFiltersObj: any = {};

    const buildObject = (filter: Filter, obj: any) => {
      if (filter.selected || filter.selected === 0) {
        obj[filter.type] = filter.selected;
      }
    };

    this.previousFilters.forEach(filter => buildObject(filter, previousFiltersObj));
    this.currentTaskListConfiguration.filters.forEach(filter => buildObject(filter, currentFiltersObj));

    if (Object.values(previousFiltersObj).length !== Object.values(currentFiltersObj).length) {
      return true;
    }

    const isOneTypeDiff = Object.entries(previousFiltersObj).some(type => type[1] !== currentFiltersObj[type[0]]);

    return isOneTypeDiff;
  }

  /**
   * Number of pending tasks is the counter of the "received" tab, in counters[0].
   */
  getNumberOfPendingTasks(): number {
    if (this.counters?.length) {
      return this.counters[0].counter;
    }
    return 0;
  }

  updateCounters(taskEvent: WebSocketTaskNotification) {
    this.counters.forEach(counter => {
      if (counter.taskIds?.length > 0) {
        const taskIndex = counter.taskIds.indexOf(taskEvent.task_id);
        if (taskIndex !== -1) {
          counter.counter--;
          counter.taskIds.splice(taskIndex, 1);
        }
      }
    });

    this.counters.forEach(counter => {
      if (
        counter.counter !== null &&
        this.isTaskMatchTaskListParams(taskEvent, counter.configuration.parameters, counter.configuration.filters)
      ) {
        counter.counter++;
        counter.taskIds.push(taskEvent.task_id);
      }
    });

    this.countersBS.next(this.counters);
  }

  /**
   * Determines if task match a TaskListConfigurationParameters, by comparing all properties.
   * Can be used for 2 things :
   * - if the task match a counter params + routing tag filter, to increment the counter.
   * - if the task match current task list params + routing tag filter, to display "new message" notification.
   *
   * @param params params of a counter or a task list
   * @param taskEvent event containing the new task
   * @returns true if task details match the params.
   */
  isTaskMatchTaskListParams(
    taskEvent: WebSocketTaskNotification,
    params: TaskListConfigurationParameters,
    filters?: Filter[]
  ) {
    // mandatory matches
    if (!params.sites_ids.includes(taskEvent.site_id)) {
      return false;
    }

    if (!params.states.includes(taskEvent.task_state)) {
      return false;
    }

    if (params.routingTagsIds.length > 0 && !params.routingTagsIds.includes(taskEvent.routing_tag_id)) {
      return false;
    }

    // optional matches
    if (
      !params.states.includes(TaskStateInWsNotif.Pending) && // pending inboxes also include the onhold state with the bot user id
      params.admin_users_ids &&
      !params.admin_users_ids.includes(taskEvent.user_id)
    ) {
      return false;
    }

    if (params.exclude_admin_users_ids && params.exclude_admin_users_ids.includes(taskEvent.user_id)) {
      return false;
    }

    // type = null : "all channels"
    // type = 0 : does not exist, so we can test truthy
    if (params.type && params.type !== taskEvent.task_type) {
      return false;
    }

    if (params.scans_ids && !params.scans_ids.includes(taskEvent.scan_id)) {
      return false;
    }

    // filter routing tag (not possible on other filters because not in websocket notif)
    let matchRoutingTag = true;
    if (filters?.length) {
      matchRoutingTag = filters.some(filter => {
        return (!filter.selected || filter.selected === taskEvent.routing_tag_id) && filter.type === FilterType.Routing;
      });
    }
    if (!matchRoutingTag) {
      return false;
    }

    return true;
  }
}
